import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  getCountFromServer,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import moment from "moment";
import Swal from "sweetalert2";
import { db, auth } from "../firebase";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
  CircularProgress,
  InputAdornment,
  Paper,
  Chip,
  Stack, IconButton
} from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import HotelIcon from "@mui/icons-material/Hotel";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PinDropIcon from "@mui/icons-material/PinDrop";
import PaymentIcon from "@mui/icons-material/Payment";
import SecurityIcon from "@mui/icons-material/Security";
import CategoryIcon from "@mui/icons-material/Category";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from "react-router-dom";

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

export default function BookingForm({ propertyId: propPropertyId, onSuccess }) {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const urlPropertyId = searchParams.get("propertyId");
  const urlPropertyName = searchParams.get("propertyName");
  const urlCheckIn = searchParams.get("checkIn");
  const urlCheckOut = searchParams.get("checkOut");
  const urlRoomTypeId = searchParams.get("roomTypeId");

  // Effective Property Metadata
  const activePropertyId = urlPropertyId;
  const activePropertyName = urlPropertyName ? decodeURIComponent(urlPropertyName) : "Property Reservation";

  // --- 2. Dates & Guests State ---
  const [checkindate, setCheckindate] = useState(
    urlCheckIn && moment(urlCheckIn, "YYYY-MM-DD", true).isValid()
      ? urlCheckIn
      : moment().format("YYYY-MM-DD")
  );
  const [checkoutdate, setCheckoutdate] = useState(
    urlCheckOut && moment(urlCheckOut, "YYYY-MM-DD", true).isValid()
      ? urlCheckOut
      : moment().add(1, "days").format("YYYY-MM-DD")
  );
  const [noofdays, setNoofdays] = useState(1);
  const [adults, setAdults] = useState(2);
  const [childs, setChilds] = useState(0);

  // --- 3. Customer State ---
  const [custname, setCustname] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("Maharashtra");
  const [pincode, setPincode] = useState("");
  const [gender, setGender] = useState("Male");
  const [visitpurpose, setVisitpurpose] = useState("Darshan");
  const [bookingsource, setBookingsource] = useState("Website");
  const [property, setProperty] = useState(null);
  const [roomtypes, setroomtypes] = useState(null);

  const [roomRates, setRoomRates] = useState([]);
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedRoomCount, setSelectedRoomCount] = useState(1);

  // --- 5. Payment & Loading State ---
  const [paymode, setPaymode] = useState("Razorpay");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Recalculate stay duration when dates change
  useEffect(() => {
    if (checkindate && checkoutdate) {
      const start = moment(checkindate);
      const end = moment(checkoutdate);
      const days = end.diff(start, "days");
      setNoofdays(days > 0 ? days : 1);
    }
  }, [checkindate, checkoutdate]);

  // Fetch Room Rates and Availability
  useEffect(() => {
    fetchRatesAndAvailability();
  }, [activePropertyId, urlRoomTypeId, checkindate, checkoutdate]);

  const fetchRatesAndAvailability = async () => {
    try {
      setIsLoading(true);

      let propData = null;
      const propRef = doc(db, 'properties', activePropertyId);
      const propSnap = await getDoc(propRef);

      if (propSnap.exists()) {
        propData = { id: propSnap.id, ...propSnap.data() };
      } else {
        const q = query(collection(db, 'properties'), where('propertyId', '==', activePropertyId));
        const qSnap = await getDocs(q);
        if (!qSnap.empty) {
          propData = { id: qSnap.docs[0].id, ...qSnap.docs[0].data() };
        }
      }
      setProperty(propData);

      let rtypeData = null;
      const propRef1 = doc(db, 'rooms', urlRoomTypeId);
      const propSnap1 = await getDoc(propRef1);

      if (propSnap1.exists()) {
        rtypeData = { id: propSnap1.id, ...propSnap1.data() };
      } else {
        const q = query(collection(db, 'rooms'), where('propertyId', '==', activePropertyId));
        const qSnap = await getDocs(q);
        if (!qSnap.empty) {
          rtypeData = { id: qSnap.docs[0].id, ...qSnap.docs[0].data() };
        }
      }
      setroomtypes(rtypeData);

      let ratesQueryConstraints = [where("propertyId", "==", activePropertyId)];
      if (urlRoomTypeId) {
        ratesQueryConstraints.push(where("roomId", "==", urlRoomTypeId));
      }

      const ratesQuery = query(collection(db, "roomrates"), ...ratesQueryConstraints);
      const ratesSnap = await getDocs(ratesQuery);
      const ratesList = ratesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      // B. Fetch room infos
      let roomInfoConstraints = [where("propertyId", "==", activePropertyId)];
      if (urlRoomTypeId) {
        roomInfoConstraints.push(where("roomId", "==", urlRoomTypeId));
      }

      const roomInfosQuery = query(collection(db, "roominfos"), ...roomInfoConstraints);
      const roomInfosSnap = await getDocs(roomInfosQuery);
      const totalRoomsList = roomInfosSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const totalCountByRoom = {};
      totalRoomsList.forEach((r) => {
        const rid = r.roomId || r.id;
        totalCountByRoom[rid] = (totalCountByRoom[rid] || 0) + 1;
      });

      // C. Calculate overlapping bookings
      const bookedDetailsQuery = query(
        collection(db, "checkindetails"),
        where("propertyId", "==", activePropertyId),
        where("status", "==", "Booked")
      );
      const bookedSnap = await getDocs(bookedDetailsQuery);

      const bookedCountByRoom = {};
      bookedSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const bIn = moment(data.checkindate);
        const bOut = moment(data.checkoutdate);
        const reqIn = moment(checkindate);
        const reqOut = moment(checkoutdate);

        if (reqIn.isBefore(bOut) && reqOut.isAfter(bIn)) {
          const rid = data.roomid;
          if (rid) {
            bookedCountByRoom[rid] = (bookedCountByRoom[rid] || 0) + 1;
          }
        }
      });

      // Build availability mapping
      const availMap = {};
      ratesList.forEach((plan) => {
        const planKey = plan.ratePlanId || plan.id;
        const roomId = plan.roomId || plan.id;
        const total = totalCountByRoom[roomId] || 5;
        const booked = bookedCountByRoom[roomId] || 0;
        const available = Math.max(0, total - booked);
        availMap[planKey] = { total, booked, available };
      });

      setRoomRates(ratesList);
      setAvailabilityMap(availMap);

      if (ratesList.length > 0 && !selectedPlanId) {
        const firstAvailable = ratesList.find(
          (p) => (availMap[p.ratePlanId || p.id]?.available || 0) > 0
        );
        const defaultPlan = firstAvailable || ratesList[0];
        setSelectedPlanId(defaultPlan.ratePlanId || defaultPlan.id);
      }
    } catch (err) {
      console.error("Error fetching rates:", err);
      Swal.fire({ icon: "error", title: "Failed to fetch room rates" });
    } finally {
      setIsLoading(false);
    }
  };

  // Find active selected plan object
  const activePlan = roomRates.find(
    (p) => (p.ratePlanId || p.id) === selectedPlanId
  );


  const activeAvail = activePlan
    ? availabilityMap[activePlan.ratePlanId || activePlan.id]?.available || 0
    : 0;

  const grandTotal = activePlan
    ? Number(activePlan.rate || 0) * Number(selectedRoomCount) * noofdays
    : 0;

  // Form Validation
  const validateForm = () => {
    const newErrors = {};

    if (!selectedPlanId) newErrors.selectedPlanId = "Please select a rate plan";
    if (selectedRoomCount <= 0) newErrors.selectedRoomCount = "Select at least 1 room";
    if (!custname.trim()) newErrors.custname = "Full name is required";

    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    if (!address.trim()) newErrors.address = "Address is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!stateName.trim()) newErrors.stateName = "State is required";

    if (!pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(pincode.trim())) {
      newErrors.pincode = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRazorpayPayment = async (bookingId) => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error("Razorpay SDK is not loaded. Check network connection."));
        return;
      }

      const options = {
        key: "rzp_live_TIpdM6KeXVoeFf",
        amount: grandTotal * 100,
        currency: "INR",
        name: activePropertyName,
        description: `Booking for ${custname}`,
        notes: { bookingId },
        handler: function (response) {
          resolve({
            razorpay_payment_id: response.razorpay_payment_id,
            status: "Success",
          });
        },
        prefill: {
          name: custname,
          email: email,
          contact: mobile,
        },
        theme: { color: "#1976d2" },
        modal: {
          ondismiss: function () {
            reject(new Error("Payment cancelled by user"));
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    });
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please correct the form errors.",
        confirmButtonColor: "#f57c00",
      });
      return;
    }

    if (!activePlan) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Selected Plan is invalid.",
        confirmButtonColor: "#f57c00",
      });
      return;
    }

    try {
      setIsLoading(true);

      // ==========================================
      // 1. SAVE CUSTOMER
      // ==========================================
      const customerData = {
        custname,
        mobile,
        email,
        address,
        city,
        state: stateName,
        pincode,
        gender,
        propertyId: activePropertyId,
        uid: auth.currentUser?.uid || "",
        createdAt: Timestamp.now(),
      };
      const customerRef = await addDoc(collection(db, "customers"), customerData);

      // ==========================================
      // 2. CHECK ROOM AVAILABILITY & ALLOCATE
      // ==========================================
      const qRes = query(
        collection(db, "reservations"),
        where("propertyId", "==", activePropertyId)
      );
      const bookingCountSnap = await getCountFromServer(qRes);
      const invoiceNo = bookingCountSnap.data().count + 1;

      const roomInfosQuery = query(
        collection(db, "roominfos"),
        where("propertyId", "==", activePropertyId),
        where("roomTypeId", "==", urlRoomTypeId)
      );
      const roomInfosSnap = await getDocs(roomInfosQuery);
      const allRooms = roomInfosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const bookedQuery = query(
        collection(db, "checkindetails"),
        where("propertyId", "==", activePropertyId),
        where("status", "in", ["Booked", "Paid", "Pending"]) // Consider pending holds as unavailable
      );
      const bookedSnap = await getDocs(bookedQuery);
      const bookedRoomNos = [];

      bookedSnap.forEach((d) => {
        const data = d.data();
        const bIn = moment(data.checkindate, "YYYY-MM-DD");
        const bOut = moment(data.checkoutdate, "YYYY-MM-DD");
        const reqIn = moment(checkindate, "YYYY-MM-DD");
        const reqOut = moment(checkoutdate, "YYYY-MM-DD");

        if (reqIn.isBefore(bOut, "day") && reqOut.isAfter(bIn, "day")) {
          bookedRoomNos.push(data.roomname);
        }
      });

      const availableRooms = allRooms.filter((r) => !bookedRoomNos.includes(r.roomNo));
      if (availableRooms.length < selectedRoomCount) {
        throw new Error("Requested rooms are no longer available.");
      }
      const assignedRooms = availableRooms.slice(0, selectedRoomCount);

      const chosenRoomsList = assignedRooms.map((room) => ({
        roomid: room.id,
        roomno: room.roomNo,
        roomname: room.roomNo,
        rtypeid: room.roomTypeId,
        roomtypename: room.roomTypeName,
        planid: activePlan.ratePlanId || activePlan.id,
        planname: activePlan.planName,
        roomrate: activePlan.rate,
        amount: activePlan.rate,
        discount: 0,
        selectedCount: 1,
      }));

      // ==========================================
      // 3. STORE INITIAL RESERVATION AS "Pending"
      // ==========================================
      const bookingPayload = {
        customerId: customerRef.id,
        invoiceno: invoiceNo,
        propertyName: activePropertyName,
        roomTypeId: urlRoomTypeId || activePlan.roomId || "",
        customers: customerData,
        rooms: chosenRoomsList,
        checkindate,
        checkoutdate,
        noofdays,
        adults: Number(adults),
        childs: Number(childs),
        noofrooms: Number(selectedRoomCount),
        visitpurpose,
        bookingsource,
        coming: "No",
        rooamount: Number(grandTotal),
        totalamount: Number(grandTotal),
        advance: 0, // 0 until payment completes
        remaining: Number(grandTotal),
        bookingDate: moment().format("YYYY-MM-DD"),
        type: "GST",
        status: "Pending", // <--- Pending status before payment
        paymode,
        paymentTransactionId: "",
        propertyId: activePropertyId,
        uid: auth.currentUser?.uid || "",
        createdAt: Timestamp.now(),
      };

      const bookingRef = await addDoc(collection(db, "reservations"), bookingPayload);

      // Save daily check-in slots as Pending
      const checkinDocRefs = [];
      for (let day = 0; day < noofdays; day++) {
        const inDate = moment(checkindate).add(day, "days").format("YYYY-MM-DD");
        const outDate = moment(checkindate).add(day + 1, "days").format("YYYY-MM-DD");

        for (const room of assignedRooms) {
          const checkinRef = await addDoc(collection(db, "checkindetails"), {
            roomid: room.id,
            roomno: room.roomNo,
            roomname: room.roomNo,
            rtypeid: room.roomTypeId,
            planid: activePlan.ratePlanId || activePlan.id,
            planname: activePlan.planName,
            roomrate: activePlan.rate,
            amount: activePlan.rate,
            bookingid: bookingRef.id,
            customerId: customerRef.id,
            checkindate: inDate,
            checkoutdate: outDate,
            propertyId: activePropertyId,
            roomTypeId: room.roomTypeId,
            roomTypeName: room.roomTypeName,
            status: "Pending", // <--- Pending
            createdAt: Timestamp.now(),
          });
          checkinDocRefs.push(checkinRef);
        }
      }

      console.log(bookingRef.id);
      
      // ==========================================
      // 4. TRIGGER PAYMENT GATEWAY
      // ==========================================
      let paymentResult = null;
      if (paymode === "Razorpay" && grandTotal > 0) {
        try {
          paymentResult = await handleRazorpayPayment(bookingRef.id);
        } catch (payErr) {
          // Payment cancelled or failed; records remain stored as "Pending" (or you can mark "Cancelled")
          throw new Error(payErr.message || "Payment process was interrupted.");
        }
      }

      // ==========================================
      // 5. POST-PAYMENT: UPDATE TO "Paid"
      // ==========================================
      await updateDoc(doc(db, "reservations", bookingRef.id), {
        status: "Booked", // <--- Transferred to Paid
        advance: Number(grandTotal),
        remaining: 0,
        paymentTransactionId: paymentResult?.razorpay_payment_id || "",
        updatedAt: Timestamp.now(),
      });

      // Update check-in day slots to Booked/Paid
      await Promise.all(
        checkinDocRefs.map((ref) =>
          updateDoc(ref, {
            status: "Booked",
            paymentStatus: "Paid",
            updatedAt: Timestamp.now(),
          })
        )
      );

      // Record payment receipt
      if (grandTotal > 0) {
        await addDoc(collection(db, "payhistory"), {
          bookingid: bookingRef.id,
          customerId: customerRef.id,
          paymode1: paymode,
          amount1: Number(grandTotal),
          paytime1: moment().format("HH:mm A"),
          transactionId: paymentResult?.razorpay_payment_id || "Direct Pay",
          type: "Booking",
          paymenttype: "B",
          status: "Completed",
          propertyId: activePropertyId,
          uid: auth.currentUser?.uid || "",
          createdAt: Timestamp.now(),
        });
      }

      // ==========================================
      // 6. SUCCESS UI & REDIRECT
      // ==========================================
      Swal.fire({
        icon: "success",
        title: "Reservation completed successfully!",
        text: "Your booking and payment have been confirmed.",
        confirmButtonColor: "#1976d2",
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          setCustname("");
          setMobile("");
          setEmail("");
          setAddress("");
          setCity("");
          setPincode("");
          setSelectedRoomCount(1);
          setErrors({});

          navigate("/thankyou", {
            state: {
              invoiceNo,
              customerName: custname,
              totalAmount: grandTotal,
              paymentMode: paymode,
              checkInDate: checkindate,
              checkOutDate: checkoutdate,
              bookingId: bookingRef.id,
            },
          });
        }
      });

      if (onSuccess) onSuccess(bookingRef.id);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Reservation Incomplete",
        text: error.message || "An error occurred while creating the reservation.",
        confirmButtonColor: "#d32f2f",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const maxAdults = Number(roomtypes?.capacity?.adults || 2);
  const maxChilds = Number(roomtypes?.capacity?.children || 0);
  const maxTotal = Number(roomtypes?.capacity?.total || maxAdults + maxChilds);

  const handleAdultsChange = (value) => {
    const count = Math.max(1, Math.min(value, maxAdults));
    setAdults(count);
  };

  const handleChildsChange = (value) => {
    const count = Math.max(0, Math.min(value, maxChilds));
    setChilds(count);
  };

  return (
    <Box sx={{ my: 3, px: { xs: 1, sm: 2 }, mx: "auto" }} className="container">
      {
        property &&
        <Paper
          elevation={0}
          sx={{
            position: 'relative', overflow: 'hidden', borderRadius: 4, mb: 4, minHeight: { xs: 440, md: 250 },
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', color: 'white', boxShadow: '0 20px 30px -10px rgba(15, 23, 42, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
          }}
        >
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${property.buildingImage})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.85)', transition: 'transform 0.5s ease-in-out', '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `linear-gradient(
            180deg, rgba(15, 23, 42, 0.3) 0%, rgba(15, 23, 42, 0.8) 60%,  rgba(15, 23, 42, 0.98) 100% )`, zIndex: 1,
            }} />
          <Box className="row"
            sx={{
              position: 'relative',
              zIndex: 2,
              p: { xs: 2.5, sm: 3.5, md: 4 },
            }}
          >

            <Stack spacing={1.5} sx={{ maxWidth: { sm: '100%', md: '100%' } }}>

              <Typography style={{ marginBottom: 80 }}
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.30rem', sm: '3.25rem', md: '3.75rem' },
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  lineHeight: 1.1,
                  alignSelf: "center",
                }}
              >
                {roomtypes?.roomTypeName}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {property.document === 'Approved' && (
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: '16px !important', color: '#38bdf8' }} />}
                    label="Verified Property"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(14, 165, 233, 0.15)',
                      color: '#38bdf8',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
                <Chip
                  label="Active Reservation"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: '#e2e8f0',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              </Stack>

              <Typography
                variant="h3"
                component="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  lineHeight: 1.1,
                }}
              >
                {property.hotelname || property.name}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: '#cbd5e1' }}>
                <LocationOnIcon sx={{ fontSize: 22, color: '#ff4d4d', mt: 0.2, flexShrink: 0 }} />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                  }}
                >
                  {[
                    property.address,
                    property.city,
                    property.state,
                    property.pincode,
                    property.country
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>


              </Stack>

            </Stack>
          </Box>
        </Paper>

      }

      <form onSubmit={handleBookingSubmit} noValidate>
        <Grid className="row">
          <Grid className="col-sm-6 mb-3">
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<PersonIcon color="primary" />}
                title={
                  <Typography fontWeight="bold">
                    Customer Details
                  </Typography>
                }
                sx={{ bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200" }}
              />
              <CardContent sx={{ p: 3 }}>
                <Grid className="row" spacing={2}>
                  <Grid className="col-sm-12 mb-3" item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Full Name *"
                      placeholder=""
                      value={custname}
                      onChange={(e) => {
                        setCustname(e.target.value);
                        if (errors.custname) setErrors((prev) => ({ ...prev, custname: "" }));
                      }}
                      error={!!errors.custname}
                      helperText={errors.custname}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mobile Number *"
                      placeholder=""
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value);
                        if (errors.mobile) setErrors((prev) => ({ ...prev, mobile: "" }));
                      }}
                      error={!!errors.mobile}
                      helperText={errors.mobile}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email Address"
                      placeholder="abc@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address *"
                      placeholder="Flat, Street, Landmark"
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        if (errors.address) setErrors((prev) => ({ ...prev, address: "" }));
                      }}
                      error={!!errors.address}
                      helperText={errors.address}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City *"
                      placeholder="City"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (errors.city) setErrors((prev) => ({ ...prev, city: "" }));
                      }}
                      error={!!errors.city}
                      helperText={errors.city}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State *"
                      value={stateName}
                      onChange={(e) => {
                        setStateName(e.target.value);
                        if (errors.stateName) setErrors((prev) => ({ ...prev, stateName: "" }));
                      }}
                      error={!!errors.stateName}
                      helperText={errors.stateName}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pincode *"
                      placeholder="400001"
                      value={pincode}
                      onChange={(e) => {
                        setPincode(e.target.value);
                        if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: "" }));
                      }}
                      error={!!errors.pincode}
                      helperText={errors.pincode}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PinDropIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid className="col-sm-12 mb-3" item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={gender}
                        label="Gender"
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid className="col-sm-6 mb-3">
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<CalendarMonthIcon color="primary" />}
                title={
                  <Typography fontWeight="bold">
                    Check-In & Guest Information
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'grey.200', bgcolor: "grey.50",
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Grid className="row" spacing={2}>
                  <Grid className="col-sm-6 mb-3" item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth disabled
                      size="small"
                      type="date"
                      label="Check-In Date"
                      value={checkindate}
                      inputProps={{ min: moment().format('YYYY-MM-DD') }}
                      onChange={(e) => setCheckindate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Check-Out Date */}
                  <Grid className="col-sm-6 mb-3" item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth disabled
                      size="small"
                      type="date"
                      label="Check-Out Date"
                      value={checkoutdate}
                      inputProps={{
                        min: moment(checkindate).add(1, 'days').format('YYYY-MM-DD'),
                      }}
                      onChange={(e) => setCheckoutdate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>


                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1, }}>
                      <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        Adults :
                      </Typography>
                      <IconButton onClick={() => handleAdultsChange(adults - 1)} disabled={adults <= 1} >
                        <RemoveIcon />
                      </IconButton>
                      <Typography fontWeight="bold">{adults}</Typography>
                      <IconButton onClick={() => handleAdultsChange(adults + 1)} disabled={adults >= maxAdults}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Maximum {maxAdults} adults
                    </Typography>
                  </Grid>

                  <Grid className="col-sm-6 mb-3" >

                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1, }}>
                      <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        Children :
                      </Typography>

                      <IconButton size="small" onClick={() => handleChildsChange(childs - 1)} disabled={childs <= 0} >
                        <RemoveIcon />
                      </IconButton>

                      <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 30, textAlign: "center", }}>
                        {childs}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleChildsChange(childs + 1)}
                        disabled={childs >= maxChilds}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Maximum {maxChilds} children
                    </Typography>
                  </Grid>

                </Grid>
              </CardContent>

              <CardHeader
                avatar={<CategoryIcon color="primary" />}
                title={
                  <Typography fontWeight="bold">
                    Select Rate Plan & Room Quantities
                  </Typography>
                }
                sx={{ bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200" }}
              />
              <CardContent sx={{ p: 3 }}>
                {isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Fetching available plans...
                    </Typography>
                  </Box>
                ) : (
                  <Grid className="row" spacing={2.5}>
                    {/* Single Plan Select Box */}
                    <Grid className="col-sm-8 mb-3" item xs={12} sm={8}>
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.selectedPlanId}
                      >
                        <InputLabel>Choose Rate Plan *</InputLabel>
                        <Select
                          value={selectedPlanId}
                          label="Choose Rate Plan *"
                          onChange={(e) => {
                            setSelectedPlanId(e.target.value);
                            setSelectedRoomCount(1);
                            if (errors.selectedPlanId)
                              setErrors((prev) => ({ ...prev, selectedPlanId: "" }));
                          }}
                        >
                          {roomRates.map((plan) => {
                            const planKey = plan.ratePlanId || plan.id;
                            const avail = availabilityMap[planKey]?.available || 0;
                            return (
                              <MenuItem
                                key={planKey}
                                value={planKey}
                                disabled={avail <= 0}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="medium">
                                    {plan.planName} — ₹{plan.rate}/night
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={avail > 0 ? `${avail} left` : "Sold Out"}
                                    color={avail > 0 ? "success" : "error"}
                                    variant="outlined"
                                    sx={{ ml: 2, height: 20, fontSize: "0.7rem" }}
                                  />
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {errors.selectedPlanId && (
                          <FormHelperText>{errors.selectedPlanId}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Quantity Dropdown */}
                    <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Number of Rooms</InputLabel>
                        <Select
                          value={selectedRoomCount}
                          label="Number of Rooms"
                          disabled={!selectedPlanId || activeAvail <= 0}
                          onChange={(e) => setSelectedRoomCount(Number(e.target.value))}
                        >
                          {Array.from(
                            { length: Math.min(activeAvail, 10) },
                            (_, i) => i + 1
                          ).map((num) => (
                            <MenuItem key={num} value={num}>
                              {num} {num === 1 ? "Room" : "Rooms"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Active Selected Plan Summary */}
                    {activePlan && (
                      <Grid className="col-sm-12 mb-3" item xs={12}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: "primary.50",
                            borderColor: "primary.light",
                            borderRadius: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                              {roomtypes?.roomTypeName} - {activePlan.planName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ₹{activePlan.rate} × {selectedRoomCount} room(s) × {noofdays} night(s)
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            Subtotal: ₹{grandTotal.toLocaleString("en-IN")}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>

              <CardHeader
                avatar={<PaymentIcon color="primary" />}
                title={
                  <Typography fontWeight="bold">
                    Payment Options & Overview
                  </Typography>
                }
                sx={{ bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200" }}
              />
              <CardContent sx={{ p: 3 }}>
                <Grid className="row" spacing={3} alignItems="center">
                  <Grid className="col-sm-12 mb-3" item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        value={paymode}
                        label="Payment Mode"
                        onChange={(e) => setPaymode(e.target.value)}
                      >
                        <MenuItem value="Razorpay">Online (Razorpay / UPI / Cards)</MenuItem>
                        {/* <MenuItem value="Cash">Cash on Check-In</MenuItem> */}
                        {/* <MenuItem value="UPI Direct">Direct UPI Transfer</MenuItem> */}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* <Grid className="col-sm-12 mb-3" item xs={12} sm={6} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Amount ({noofdays} Night{noofdays > 1 ? "s" : ""})
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary.main">
                      ₹{grandTotal.toLocaleString("en-IN")}
                    </Typography>
                  </Grid> */}

                  <Grid className="col-sm-12 mb-3" item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={isLoading || grandTotal === 0}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SecurityIcon />
                        )
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                      }}
                    >
                      {isLoading
                        ? "Processing Reservation..."
                        : `Confirm & Pay ₹${grandTotal.toLocaleString("en-IN")}`}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </form>
    </Box>
  );
}